"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
exports.syncMessages = syncMessages;
const utils_1 = require("@aws-amplify/core/internals/utils");
const pinpoint_1 = require("@aws-amplify/core/internals/providers/pinpoint");
const core_1 = require("@aws-amplify/core");
const pinpoint_2 = require("@aws-amplify/core/internals/aws-clients/pinpoint");
const utils_2 = require("../utils");
const errors_1 = require("../../../errors");
const utils_3 = require("../../../utils");
/**
 * Fetch and persist messages from Pinpoint campaigns.
 * Calling this API is necessary to trigger InApp messages on the device.
 *
 * @deprecated AWS will end support for Amazon Pinpoint on October 30, 2026.
 *
 * @throws service exceptions - Thrown when the underlying Pinpoint service returns an error.
 * @throws validation: {@link InAppMessagingValidationErrorCode} - Thrown when the provided parameters or library
 * configuration is incorrect, or if In App messaging hasn't been initialized.
 * @returns A promise that will resolve when the operation is complete.
 * @example
 * ```ts
 * // Sync InApp messages with Pinpoint and device.
 * await syncMessages();
 *
 * ```
 */
async function syncMessages() {
    (0, utils_3.assertIsInitialized)();
    const messages = await fetchInAppMessages();
    if (!messages || messages.length === 0) {
        return;
    }
    try {
        const key = `${utils_2.PINPOINT_KEY_PREFIX}${utils_2.STORAGE_KEY_SUFFIX}`;
        await core_1.defaultStorage.setItem(key, JSON.stringify(messages));
    }
    catch (error) {
        (0, errors_1.assertServiceError)(error);
        throw error;
    }
}
async function fetchInAppMessages() {
    try {
        const { credentials, identityId } = await (0, utils_2.resolveCredentials)();
        const { appId, region } = (0, utils_2.resolveConfig)();
        const endpointId = await (0, pinpoint_1.resolveEndpointId)({
            appId,
            category: utils_2.CATEGORY,
            channelType: utils_2.CHANNEL_TYPE,
            credentials,
            identityId,
            region,
            userAgentValue: (0, utils_2.getInAppMessagingUserAgentString)(utils_1.InAppMessagingAction.SyncMessages),
        });
        const input = {
            ApplicationId: appId,
            EndpointId: endpointId,
        };
        const response = await (0, pinpoint_2.getInAppMessages)({ credentials, region }, input);
        const { InAppMessageCampaigns: messages } = response.InAppMessagesResponse ?? {};
        return messages;
    }
    catch (error) {
        (0, errors_1.assertServiceError)(error);
        throw error;
    }
}
